'use client'

import { useProductExtraFeatures } from '@/hooks/react-query/products/use-product'
import { sanitize } from '@/lib/helpers/sanitize'
import { ExtraFeature } from '@/types/product-detail'
import Link from 'next/link'
import { ReactNode, useState } from 'react'
import { useProductDetailSafe } from '../../context/product-detail-context'

type ProductInfoPolicyProps = {
  gender: string
  collections: Array<{ value: string } | string>
}

const OUTLET_COLLECTION_ID = '63b8e548654bd10f5d7fe16c'
const RETURN_POLICY_URL = '/page/dich-vu-60-ngay-doi-tra'

// Title feature "Bảo quản" từ API get-extra-features (cùng nguồn bảng thông số
// ở phần mô tả sản phẩm) → tách sang accordion riêng
const CARE_FEATURE_TITLE = 'bảo quản'

const DEFAULT_CARE_INSTRUCTIONS = [
  'Giặt máy ở chế độ nhẹ nhàng, nhiệt độ dưới 30°C.',
  'Không sử dụng chất tẩy mạnh hoặc clo.',
  'Không sấy ở nhiệt độ cao, phơi trong bóng râm thoáng mát.',
  'Lộn trái sản phẩm khi giặt và phơi để giữ màu lâu hơn.',
]

const isOutletCollection = (
  collections: Array<{ value: string } | string>
): boolean => {
  return collections.some((col) =>
    typeof col === 'string'
      ? col === OUTLET_COLLECTION_ID
      : col.value === OUTLET_COLLECTION_ID
  )
}

const isCareFeature = (feature: ExtraFeature) =>
  feature.title?.trim().toLowerCase() === CARE_FEATURE_TITLE

const hasValues = (feature: ExtraFeature) =>
  feature.values?.some((v) => v?.title?.trim())

export const ProductInfoPolicy = ({
  collections = [],
}: ProductInfoPolicyProps) => {
  const { product } = useProductDetailSafe()
  const { data: features = [] } = useProductExtraFeatures(product?.id || '')

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    spec: true,
    care: true,
    return: true,
  })

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const validFeatures = features.filter(hasValues)
  const specFeatures = validFeatures.filter((f) => !isCareFeature(f))
  const careFeature = validFeatures.find(isCareFeature)
  const careInstructions = careFeature
    ? careFeature.values.map((v) => v.title).filter(Boolean)
    : DEFAULT_CARE_INSTRUCTIONS

  // Fallback khi API features chưa có data (sản phẩm cũ chưa nhập thông số)
  const shape = product?.shape_type?.label
  const fallbackSpecs = [
    { title: 'Chất liệu', value: product?.short_desc },
    { title: 'Kiểu dáng', value: shape },
  ].filter((item) => !!item.value)

  const isOutlet = isOutletCollection(collections)
  const hasSpecContent = specFeatures.length > 0 || fallbackSpecs.length > 0

  return (
    <div
      className='w-full border-b border-neutral-800'
      id='product-detail-policy'
    >
      {hasSpecContent && (
        <PolicyAccordion
          title='Chất liệu & kiểu dáng'
          isOpen={openSections.spec}
          onToggle={() => toggleSection('spec')}
        >
          <ul className='flex flex-col gap-2'>
            {specFeatures.length > 0
              ? specFeatures.map((feature, index) => (
                  <SpecRow
                    key={feature.title || `feature-${index}`}
                    title={feature.title}
                  >
                    {feature.values.map((item, idx) =>
                      item.link ? (
                        <Link
                          key={`${item.title}-${idx}`}
                          href={item.link}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='block text-primary underline hover:text-primary-600'
                        >
                          {item.title}
                        </Link>
                      ) : (
                        <p key={`${item.title}-${idx}`}>{item.title}</p>
                      )
                    )}
                  </SpecRow>
                ))
              : fallbackSpecs.map((item) => (
                  <SpecRow key={item.title} title={item.title}>
                    <p>{item.value}</p>
                  </SpecRow>
                ))}
          </ul>
        </PolicyAccordion>
      )}

      {careInstructions.length > 0 && (
        <PolicyAccordion
          title='Bảo quản'
          isOpen={openSections.care}
          onToggle={() => toggleSection('care')}
        >
          <ul className='list-disc ps-[21px] text-sm leading-5 text-neutral-800'>
            {careInstructions.map((text, idx) => (
              <li key={`${text}-${idx}`}>{text}</li>
            ))}
          </ul>
        </PolicyAccordion>
      )}

      {!isOutlet && (
        <PolicyAccordion
          title='Đổi trả 60 ngày'
          isOpen={openSections.return}
          onToggle={() => toggleSection('return')}
        >
          <ul className='list-disc ps-[21px] text-sm leading-5 text-neutral-800'>
            <li>
              Áp dụng theo chính sách đổi trả 60 ngày của Coolmate.{' '}
              <Link
                href={RETURN_POLICY_URL}
                target='_blank'
                className='underline hover:text-neutral-600'
              >
                Xem chính sách
              </Link>
            </li>
          </ul>
        </PolicyAccordion>
      )}
    </div>
  )
}

const PolicyAccordion = ({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string
  isOpen: boolean
  onToggle: () => void
  children: ReactNode
}) => (
  <div className='flex flex-col gap-3 border-t border-neutral-200 py-5 lg:gap-4'>
    <button
      type='button'
      className='flex w-full items-center justify-between text-left text-neutral-800'
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span className='font-sans text-base font-medium leading-6'>{title}</span>
      <span className='font-sans text-lg leading-6' aria-hidden>
        {isOpen ? '−' : '+'}
      </span>
    </button>
    {isOpen && children}
  </div>
)

const SpecRow = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) => (
  <li className='flex flex-col gap-3 font-sans text-sm leading-5 lg:flex-row lg:items-start lg:gap-4'>
    <span
      className='shrink-0 text-neutral-500 lg:w-[110px]'
      dangerouslySetInnerHTML={{ __html: sanitize(title) || '' }}
    />
    <div className='flex-1 text-neutral-800'>{children}</div>
  </li>
)
